import React from "react";
import { Reveal } from "../../lib/motion";
import { ArrowRight, LayoutDashboard, Users, UserRound, TrendingUp, Calendar, ListChecks, UserCog, FileText, Zap, BarChart3 } from "lucide-react";
import { useApp } from "@/context/AppContext";

const MODULE_ICONS = {
  Dashboard: LayoutDashboard, Leads: Users, Customers: UserRound, Pipeline: TrendingUp,
  Bookings: Calendar, Tasks: ListChecks, Team: UserCog, Documents: FileText,
  Automations: Zap, Reporting: BarChart3,
};

export default function CrmExample() {
  const { t, formatPrice } = useApp();
  const modules = t("example.modules") || [];
  const stats = t("example.stats") || [];
  // Illustrative pipeline value — base GBP amount, rounded to nearest 500 per currency.
  const renderStatValue = (raw) => {
    if (raw === "__PIPELINE__") return formatPrice(40000, { base: "GBP", round: 500 });
    return raw;
  };
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="crm" data-testid="crm-example-section" className="relative py-20 md:py-28">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.24em] uppercase text-black/75 mb-4 font-bold">
            {t("example.eyebrow")}
          </p>
          <h2 className="font-display font-black text-black leading-[1.02] tracking-tight text-4xl md:text-5xl lg:text-6xl">
            {t("example.title1")}<br />
            <span className="text-black/55">{t("example.title2")}</span>
          </h2>
        </Reveal>

        <Reveal>
          <div
            data-testid="crm-mock-dashboard"
            className="mt-12 rounded-3xl overflow-hidden border border-black/15 bg-white shadow-[0_50px_100px_-40px_rgba(0,0,0,0.4)]"
          >
            {/* macOS-style chrome */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-black/10 bg-[#f7f4f0]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              <div className="ml-4 flex-1 h-6 rounded bg-black/5 flex items-center px-3">
                <span className="text-[11px] font-mono text-black/60">app.orangebysite.com/dashboard</span>
              </div>
            </div>

            <div className="grid grid-cols-12 min-h-[420px]">
              {/* Sidebar with modules */}
              <aside className="col-span-4 md:col-span-3 border-r border-black/10 bg-[#fafaf7] p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-black/55 font-bold px-2 mb-3">Modules</div>
                <nav className="flex flex-col gap-1">
                  {modules.map((m, i) => {
                    const Icon = MODULE_ICONS[m] || LayoutDashboard;
                    const active = i === 0;
                    return (
                      <div
                        key={m}
                        data-testid={`crm-module-${m.toLowerCase()}`}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold ${
                          active ? "bg-black text-white" : "text-black/80 hover:bg-black/5"
                        }`}
                      >
                        <Icon size={14} strokeWidth={2.2} />
                        {m}
                      </div>
                    );
                  })}
                </nav>
              </aside>

              {/* Main dashboard panel */}
              <div className="col-span-8 md:col-span-9 p-6 md:p-8 bg-white">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h3 className="font-display font-black text-2xl md:text-3xl text-black tracking-tight">Today</h3>
                  <span className="text-[11px] text-black/60 font-semibold">{t("example.illustrative")}</span>
                </div>

                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {stats.map((s) => (
                    <div
                      key={s.label}
                      data-testid={`crm-stat-${s.label.toLowerCase().replace(/\s+/g,"-")}`}
                      className="rounded-2xl border border-black/10 p-4 bg-[#fffaf3]"
                    >
                      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/60">
                        {s.label}
                      </div>
                      <div className="mt-2 font-display font-black text-2xl md:text-3xl text-black tracking-tight" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {renderStatValue(s.value)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Simple pipeline bar mock */}
                <div className="mt-8">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-black/60 mb-3">Pipeline</div>
                  <div className="grid grid-cols-5 gap-2 items-end h-24">
                    {[70, 92, 56, 78, 44].map((h, i) => (
                      <div key={i} className="flex flex-col items-center gap-1.5">
                        <div className="w-full rounded-md bg-gradient-to-t from-[#ff7a1f] to-[#ffb347]" style={{ height: `${h}%` }} />
                        <div className="text-[10px] font-semibold text-black/60">{["New","Qual.","Prop.","Neg.","Won"][i]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <p className="max-w-2xl text-black/80 text-base md:text-lg leading-relaxed">
              {t("example.sub")}
            </p>
            <button
              data-testid="crm-example-cta"
              onClick={() => go("#discovery")}
              className="group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-black text-white font-display font-bold text-sm md:text-base tracking-wide uppercase hover:bg-black/90 transition-all shadow-[0_20px_45px_-12px_rgba(0,0,0,0.45)] shrink-0"
            >
              {t("example.cta")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform rtl:-scale-x-100" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
